import { OP_CODE_GROUPS } from "../globals/globals";
import { IGeoRegion, IProfile } from "../store/api/admin/types";
import { IGeoRegionTable, ISiteGroup } from "../store/api/devices/types";

export default class ProfilePermissions {
  private adminOpCodeGroups = Object.keys(OP_CODE_GROUPS).filter((key) => !!OP_CODE_GROUPS[key].isAdmin);
  private appOpCodeGroups = Object.keys(OP_CODE_GROUPS).filter((key) => !OP_CODE_GROUPS[key].isAdmin);

  private profile: IProfile;
  constructor(profile: IProfile) {
    this.profile = profile;
  }

  getDomainList(): string[] {
    return this.profile.managementDomains || [];
  }

  getOpCodeGroups(): string[] {
    return this.profile.opCodeGroups || [];
  }

  getDomainOpCodeGroups(domain: string): string[] | null {
    if (["", "*"].includes(domain)) {
      return null;
    }

    if (!this.profile.domainOpCodeGroups || this.profile.domainOpCodeGroups[domain] == null) {
      return null;
    }

    return this.profile.domainOpCodeGroups[domain];
  }

  isOverride(domain: string): boolean {
    if (["", "*"].includes(domain)) {
      return false;
    }

    if (this.profile.domainOpCodeGroups == null) return false;

    return this.profile.domainOpCodeGroups[domain] != null;
  }

  hasAdminPermissions(): boolean {
    const allOpCodeGroups = this.getAllOpCodesGroups();
    if (allOpCodeGroups == null) {
      return false;
    }

    return allOpCodeGroups.some((opCodeGroup) => this.adminOpCodeGroups.includes(opCodeGroup));
  }

  hasDomainFilter(): boolean {
    const domainNameList = this.getDomainList();
    if (domainNameList == null || domainNameList.length === 0) return false;

    if (domainNameList.length === 1 && ["", "*"].includes(domainNameList[0])) {
      return false;
    }

    return true;
  }

  getProfileId(): number {
    return this.profile.id || 0;
  }

  getProfileName(): string {
    return this.profile.name;
  }

  getTaskTypes(domain: string): string[] {
    if (this.profile.taskTypes == null) {
      return [];
    }
    return this.profile.taskTypes[domain];
  }

  getGeoRegions(): IGeoRegion[] {
    if (this.profile.geoRegions == null || Object.keys(this.profile.geoRegions).length === 0) {
      return [];
    }
    return this.profile.geoRegions;
  }

  getSiteGroups(): ISiteGroup[] {
    const geoRegions = this.getGeoRegions();
    if (geoRegions.length === 0) return [];
    const siteGroups: ISiteGroup[] = [];
    for (const region of geoRegions) {
      if (region.siteGroups && region.siteGroups.length) {
        for (const siteGroup of region.siteGroups) {
          siteGroups.push(siteGroup);
        }
      }
    }

    return siteGroups;
  }

  getAdminOperations(domain: string): string[] {
    let response: string[] = [];

    const opCodeGroups = this.getDomainOpCodeGroups(domain);
    if (opCodeGroups == null) {
      return this.getOpCodeGroups();
    }

    opCodeGroups.forEach((opCodeGroup) => {
      if (this.adminOpCodeGroups.includes(opCodeGroup) && !response.includes(opCodeGroup)) {
        response.push(opCodeGroup);
      }
    });

    return response;
  }

  getAppOperations(domain: string): string[] {
    let response: string[] = [];

    const opCodeGroups = this.getDomainOpCodeGroups(domain);
    if (opCodeGroups == null) {
      return this.getOpCodeGroups();
    }

    opCodeGroups.forEach((opCodeGroup) => {
      if (this.appOpCodeGroups.includes(opCodeGroup) && !response.includes(opCodeGroup)) {
        response.push(opCodeGroup);
      }
    });

    return response;
  }

  getAllGeoRegionsForTable(geoRegionsFromSiteGroup?: IGeoRegionTable[]): IGeoRegionTable[] {
    let geoRegions = this.getGeoRegions();
    if (geoRegions.length === 0 && geoRegionsFromSiteGroup?.length === 0) return [];
    if (!!geoRegionsFromSiteGroup && geoRegionsFromSiteGroup?.length > 0) {
      geoRegions = geoRegionsFromSiteGroup;
    }
    const regionData: IGeoRegionTable[] = [];

    for (const region of geoRegions) {
      let deviceCount: number = 0;
      let disconnectedDeviceCount: number = 0;
      let offlineDeviceCount: number = 0;
      let kpiSyncErrorCount: number = 0;

      if (region.siteGroups && region.siteGroups.length) {
        for (const siteGroup of region.siteGroups) {
          if (!!siteGroup?.deviceCount) {
            deviceCount = deviceCount + siteGroup.deviceCount;
          }
          if (!!siteGroup?.disconnectedDeviceCount) {
            disconnectedDeviceCount += siteGroup.disconnectedDeviceCount;
          }
          if (!!siteGroup?.offlineDeviceCount) {
            offlineDeviceCount += siteGroup.offlineDeviceCount;
          }
          if (!!siteGroup?.kpiSyncErrorCount) {
            kpiSyncErrorCount += siteGroup.kpiSyncErrorCount;
          }
        }
      }
      regionData.push({ name: region.name, objectKey: region.objectKey, deviceCount, disconnectedDeviceCount, offlineDeviceCount, kpiSyncErrorCount });
    }
    return regionData;
  }

  /*
  [
    {
        "profileId": 1,
        "name": "Admin",
        "profileName": "Admin",
        "taskTypes": {
            "": [
                "all"
            ]
        },
        "domainList": [],
        "opCodeGroups": [
            "acknowledge_events",
            "manage_topology",
            "configure_profile",
            "user_management",
            "system_admin",
            "view_topology",
            "view_fc_tca",
            "view_performance",
            "view_profile",
            "view_sn",
            "view_tasks",
            "manage_sn",
            "manage_fc_tca",
            "view_events",
            "performance_management",
            "task_management"
        ],
        "domainOpCodeGroups": {}
    },
    {
        "profileId": 8,
        "name": "profile3",
        "profileName": "profile3",
        "taskTypes": {},
        "domainList": [
            "Domain1",
            "Domain2"
        ],
        "opCodeGroups": [
            "view_topology",
            "view_profile",
            "view_sn",
            "view_tasks"
        ],
        "domainOpCodeGroups": {
            "Domain2": [
                "acknowledge_events",
                "view_topology",
                "view_fc_tca",
                "view_profile",
                "manage_fc_tca",
                "view_events"
            ]
        }
    }
]
*/

  getAllOpCodesGroups(): string[] {
    if (this.getDomainList().length === 0 || (!!this.profile?.domainOpCodeGroups && Object.keys(this.profile.domainOpCodeGroups).length <= 0)) {
      return this.getOpCodeGroups();
    }
    const opCodeGroups = this.getOpCodeGroups();
    if (this.profile.domainOpCodeGroups) {
      return Object.values(this.profile.domainOpCodeGroups).reduce<string[]>((acc, opCodes) => [...opCodeGroups, ...acc, ...opCodes], []);
    }

    return [];
  }

  hasOpCodeGroup(opCode: string): boolean {
    const allOpCodeGroups = this.getAllOpCodesGroups();
    if (allOpCodeGroups == null) {
      return false;
    }

    return allOpCodeGroups.includes(opCode);
  }

  hasOpCodeGroupForDomain(opCodeGroup: string, domainKey: string | undefined): boolean {
    if (!domainKey || domainKey === "") return false;

    if (!this.hasDomainFilter() || !this.isOverride(domainKey)) {
      return this.getOpCodeGroups().includes(opCodeGroup);
    }

    const opCodeGroups = this.getDomainOpCodeGroups(domainKey);
    if (opCodeGroups) {
      return opCodeGroups.includes(opCodeGroup);
    }

    return false;
  }
}
